<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div ref="crefwrapper" class="carousel-wrapper" role="region" aria-roledescription="carousel">
    <button
      aria-label="slide left"
      class="button-slide-left"
      :disabled="scrollIndex === 0"
      :class="{ disabled: scrollIndex === 0 }"
      @click="changeSlide(-1)"
    >
      <IconArrow direction="left" />
    </button>

    <div ref="carousel" class="carousel" tabindex="0">
      <Card
        v-for="(item, index) in message.data.items"
        :key="index"
        ref="carouselItems"
        :card="item"
        :class="{ active: index === scrollIndex }"
        :aria-hidden="index !== scrollIndex ? 'true' : 'false'"
        :inert="index !== scrollIndex"
        @on-intent="($event) => $emit('onIntent', $event)"
      />
    </div>
    <button
      aria-label="slide right"
      class="button-slide-right"
      :disabled="scrollIndex === message.data.items.length - 1"
      :class="{ disabled: scrollIndex === message.data.items.length - 1 }"
      @click="changeSlide(1)"
    >
      <IconArrow />
    </button>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, useTemplateRef, watch } from "vue";
import Card from "../Card/Card.vue";
import { useDebounceFn, useEventListener, useFocusWithin, useMagicKeys } from "@vueuse/core";
import IconArrow from "@/svg/IconArrow.vue";
import type { Carousel } from "@/services/bot/messageBuilder";
import type { Intent } from "@/types";

defineProps<{
  message: Carousel;
}>();
defineEmits<{
  onIntent: [intent: Intent];
}>();

const carousel = useTemplateRef("carousel");
const carouselItems = useTemplateRef("carouselItems");

const scrollIndex = ref(-1);
const { arrowLeft, arrowRight /* keys you want to monitor */ } = useMagicKeys();
const { focused } = useFocusWithin(carousel);

const scrollAmount = 300;
watch(arrowLeft, (v) => {
  if (v && focused.value) {
    carousel.value?.scrollBy({ left: -scrollAmount, behavior: "smooth" });
  }
});
watch(arrowRight, (v) => {
  if (v && focused.value) {
    carousel.value?.scrollBy({ left: scrollAmount, behavior: "smooth" });
  }
});

function changeSlide(direction: number) {
  console.log("changeSlide", direction);
  if (carousel.value === null || carouselItems.value === null) {
    return;
  }
  if (direction < 0) {
    carousel.value?.scrollBy({ left: -scrollAmount, behavior: "smooth" });
  } else {
    carousel.value?.scrollBy({ left: scrollAmount, behavior: "smooth" });
  }
}

/**
 * Determines the index of the list item that is currently
 * most centered within the carousel's visible area.
 */
function getCenteredVisibleItemIndex() {
  if (carousel.value === null || carouselItems.value === null) {
    return -1;
  }
  const carouselRect = carousel.value.getBoundingClientRect();
  // Calculate the horizontal center of the carousel's visible area
  const carouselCenter = (carouselRect.left + carouselRect.right) / 2;

  let closestItemIndex = -1;
  let minDistance = Infinity;

  carouselItems?.value?.forEach((item, index) => {
    const itemRect = item?.$el.getBoundingClientRect();

    // Check if the item is at least partially visible within the carousel's horizontal bounds
    const isPartiallyVisible =
      itemRect.right > carouselRect.left && itemRect.left < carouselRect.right;

    if (isPartiallyVisible) {
      // Calculate the horizontal center of the current item
      const itemCenter = (itemRect.left + itemRect.right) / 2;
      // Calculate the distance between the item's center and the carousel's center
      const distance = Math.abs(itemCenter - carouselCenter);

      // If this item is closer to the center than the previous closest
      if (distance < minDistance) {
        minDistance = distance;
        closestItemIndex = index;
      }
    }
  });

  return closestItemIndex;
}

const calcCurrentIndex = useDebounceFn(() => {
  const visibleIndex = getCenteredVisibleItemIndex();
  scrollIndex.value = visibleIndex;
}, 50);

onMounted(() => {
  calcCurrentIndex();
  useEventListener(carousel?.value, "scroll", calcCurrentIndex);
});
</script>

<style scoped lang="scss">
.carousel-wrapper {
  position: relative;
  overflow: hidden;
  // this is a hack because the conversation container has some padding
  // to align the carousel with the rest of the conversation
  // we need to remove that padding
  max-width: calc(100% + 20px);
  margin: 0 -10px;
}

.carousel {
  position: relative;
  list-style: none;
  margin: 10px 0;
  padding: 0 12px;

  display: grid;
  gap: 6px;
  grid-auto-flow: column;
  grid-auto-columns: 90%;

  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;

  @media (prefers-reduced-motion: no-preference) {
    scroll-behavior: smooth;
  }

  /* anchor-name: --carousel; */

  > div {
    scroll-snap-align: center;
    display: grid;
    grid-template-rows: subgrid;
    grid-row: span 3;
    /*
    // DEBUG
    &.active {
      background-color: red;
    } */
  }

  /* &::scroll-button(*),
  &.button-slide-left,
  &.button-slide-right {
    position: fixed;
    position-anchor: --carousel;
    margin: 1rem;
    font: inherit;
    background: dodgerblue;
    border: 0;
    border-radius: 4px;
    font-weight: 700;
  } */

  /* &::scroll-button(right), */
  /* &.button-slide-left {
    content: "right";
    position-area: center right;
  } */

  /* &::scroll-button(left) {
    content: "left";
    position-area: center left;
  } */
}

.button-slide-left,
.button-slide-right {
  position: absolute;
  /* position-anchor: --carousel; */
  margin: 1rem;

  color: white;
  border: 0;
  border-radius: 50%;
  font-weight: 700;
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  background: var(--moin-message-carousel-button-background, #00000099);
  backdrop-filter: blur(4px);

  &:hover {
    background: var(--moin-message-carousel-button-background, #000000);
  }

  &.disabled {
    opacity: 0;
  }
}

/* &::scroll-button(right), */
.button-slide-right {
  /* content: "right"; */
  /* position-area: center right; */
  right: 0;
  top: 50%;
}

.button-slide-left {
  /* position-area: center left; */
  /* left: -32px; */
  top: 50%;
  z-index: 100;
}

button:focus-visible {
  outline: solid 2px black;
  background: var(--moin-message-carousel-button-background, #000000);
  outline-offset: 2px;
}
</style>
